import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Users, Mail, Building2, Calendar, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function formatDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return s; }
}

export default function AdminPartnersPage() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/partners");
      setPartners(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id, name) => {
    try {
      const { data } = await api.delete(`/admin/partners/${id}`);
      toast.success(`${name} supprimé (${data.events_deleted} événement${data.events_deleted > 1 ? "s" : ""} aussi supprimé${data.events_deleted > 1 ? "s" : ""})`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = q
    ? partners.filter((p) =>
        [p.name, p.email, p.organization].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))
      )
    : partners;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 fade-in">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted_text hover:text-sage mb-6" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
      </Link>

      <div className="mb-8">
        <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-1">Administration</div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-ink flex items-center gap-3">
          <Users className="w-8 h-8 text-sage" /> Partenaires inscrits
        </h1>
        <p className="text-muted_text mt-1">
          {partners.length} partenaire{partners.length > 1 ? "s" : ""} {partners.length > 1 ? "inscrits" : "inscrit"} sur la plateforme.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-[#F0E0CC] overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-[#F0E0CC]">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted_text" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher par nom, email ou organisation…"
              className="pl-11 h-11 rounded-xl border-[#F0E0CC] focus-visible:ring-sage/20 focus-visible:border-sage"
              data-testid="partner-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-muted_text">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 sm:p-16 text-center" data-testid="empty-partners">
            <Users className="w-10 h-10 mx-auto text-muted_text mb-3" />
            <h3 className="font-heading font-semibold text-lg text-ink">
              {q ? "Aucun partenaire trouvé" : "Aucun partenaire inscrit"}
            </h3>
            <p className="text-sm text-muted_text mt-1">
              {q ? "Essayez avec d'autres mots-clés." : "Les partenaires apparaîtront ici dès leur inscription."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[#F0E0CC]">
            {filtered.map((p) => (
              <li key={p.id} className="p-4 sm:p-5 flex flex-col lg:flex-row gap-4 hover:bg-[#FEFCE8]/50" data-testid="partner-row">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center text-sage font-heading font-bold text-lg">
                    {(p.name || "?").charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-ink">{p.name}</h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted_text mt-1">
                    <span className="inline-flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" /> {p.email}
                    </span>
                    {p.organization && (
                      <span className="inline-flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5" /> {p.organization}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" /> Inscrit le {formatDate(p.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 lg:self-center">
                  <div className="flex gap-2 text-xs">
                    <span className="px-2.5 py-1 rounded-full bg-[#D1FAE5] text-[#065F46] font-semibold" title="Événements validés">
                      {p.events_validated} validé{p.events_validated > 1 ? "s" : ""}
                    </span>
                    {p.events_pending > 0 && (
                      <span className="px-2.5 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold" title="Événements en attente">
                        {p.events_pending} en attente
                      </span>
                    )}
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-xl border-[#F0E0CC] text-red-600" data-testid="delete-partner-button">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce partenaire ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          <strong>{p.name}</strong> ({p.email}) et l'ensemble de ses{" "}
                          <strong>{p.events_total} événement{p.events_total > 1 ? "s" : ""}</strong> seront définitivement supprimés. Cette action est irréversible.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(p.id, p.name)} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-partner">
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
